import React, { useState, useCallback, useEffect } from 'react';
import { Container, Grid, Box, Typography, Tabs, Tab } from '@mui/material';
import NotificationsTable from '../notifications/NotificationsTable';
import NotificationForm from '../notifications/NotificationForm';
import NotificationPreviewPanel from '../notifications/NotificationPreviewPanel';
import IngestsTable from '../ingests/IngestsTable';
import IngestForm from '../ingests/IngestForm';
import { useGlobal } from '../contexts/GlobalContext';

const NotificationsService = () => {
  const { setCurrentContext } = useGlobal();
  const [tab, setTab] = useState('ingestion');

  // Delivery (existing notifications) state
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [notificationsRefreshKey, setNotificationsRefreshKey] = useState(0);

  // Ingestion (alpha overlays) state
  const [selectedIngest, setSelectedIngest] = useState(null);
  const [ingestsRefreshKey, setIngestsRefreshKey] = useState(0);

  const handleNotificationSaved = useCallback((saved) => {
    setNotificationsRefreshKey((k) => k + 1);
    if (saved && saved.notification_id) setSelectedNotification(saved);
  }, []);
  const handleNotificationSelect = useCallback((row) => {
    setSelectedNotification((prev) => (prev?.notification_id === row.notification_id ? null : row));
  }, []);
  const handleNotificationClear = useCallback(() => setSelectedNotification(null), []);
  const handleNotificationsChanged = useCallback(() => setNotificationsRefreshKey((k) => k + 1), []);

  const handleIngestSaved = useCallback((saved) => {
    setIngestsRefreshKey((k) => k + 1);
    if (saved && saved.overlay_id) setSelectedIngest(saved);
  }, []);
  const handleIngestSelect = useCallback((row) => {
    setSelectedIngest((prev) => (prev?.overlay_id === row.overlay_id ? null : row));
  }, []);
  const handleIngestClear = useCallback(() => setSelectedIngest(null), []);
  const handleIngestsChanged = useCallback(() => setIngestsRefreshKey((k) => k + 1), []);

  useEffect(() => {
    setCurrentContext({ product: 'notify', page: tab });
    return () => setCurrentContext(null);
  }, [setCurrentContext, tab]);

  return (
    <Container maxWidth="xl" sx={{ mt: 2 }}>
      <Box sx={{ mb: 1 }}>
        <Typography variant="h5">Notifications</Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>Ingestion</strong> defines what signals get generated (per-account overlays on alpha&apos;s sources).
          {' '}<strong>Delivery</strong> takes those signals (plus alpha defaults) and pushes them to your webhook.
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab label="Ingestion" value="ingestion" />
        <Tab label="Delivery" value="delivery" />
      </Tabs>

      {tab === 'ingestion' && (
        <Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Pick an alpha preset (<code>hyperliquid</code>, <code>polymarket</code>, <code>dex</code>, <code>kalshi</code>),
              inherit every default, and override signal-type kwargs or add new signal_types that
              reuse alpha&apos;s detectors. Activated overlays feed alpha&apos;s existing pipeline for this account only.
            </Typography>
          </Box>
          <Grid container spacing={2} sx={{ alignItems: 'flex-start' }}>
            <Grid size={{ xs: 12, md: 7 }}>
              <IngestsTable
                selected={selectedIngest}
                onSelect={handleIngestSelect}
                refreshKey={ingestsRefreshKey}
                onChanged={handleIngestsChanged}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <IngestForm
                editing={selectedIngest}
                onSaved={handleIngestSaved}
                onClear={handleIngestClear}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {tab === 'delivery' && (
        <Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Compose 1-N algos into a single webhook subscription. Build the algos in <strong>Deploy → Algos</strong>
              {' '}(pick one of the <code>alpha-notifications-*</code> indexes), then pick them here, attach a webhook + guards, and activate.
            </Typography>
          </Box>
          <Grid container spacing={2} sx={{ alignItems: 'flex-start' }}>
            <Grid size={{ xs: 12, md: 7 }}>
              <NotificationsTable
                selected={selectedNotification}
                onSelect={handleNotificationSelect}
                refreshKey={notificationsRefreshKey}
                onChanged={handleNotificationsChanged}
              />
              <Box sx={{ mt: 2 }}>
                <NotificationPreviewPanel notification={selectedNotification} />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <NotificationForm
                editing={selectedNotification}
                onSaved={handleNotificationSaved}
                onClear={handleNotificationClear}
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </Container>
  );
};

export default NotificationsService;
